// =============================================================================
// NewHopSubmissionScreen — /projects/:projectId/hop/submit/new
//
// Manager-side builder for a HOP submission. Single-page, scroll-down:
//   1. Title + notes
//   2. HOP packs — pick which to include (multi-select)
//   3. Photos — for each photo in selected packs: include + bundle number
//   4. Documents — pick from HOP-pack docs + project docs, assign doc_type
//   5. Recipients — multi-select from client_contacts
//   6. Submit → creates draft, inserts all child rows, calls worker to mint
//      magic token, shows modal with the accept URL + emails to copy/paste.
// =============================================================================

import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, FileText, Send, Users, Check, AlertTriangle,
  ChevronDown, ChevronRight, Folder, Mail, Copy, ExternalLink, Eye, X,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Spinner, Pill, EmptyState } from "@/components/ui";
import { getAssetSignedUrl } from "@/features/hop/hopUpload";
import { useAcceptanceBasket } from "./useAcceptanceBasket";

const DOC_TYPES = [
  { value: "rlm_pdf",       label: "Red Line Markup (PDF)" },
  { value: "rlm_xlsx",      label: "As-Built / RLM (Excel)" },
  { value: "sweep_test",    label: "Sweep test (Anritsu)" },
  { value: "concrete_test", label: "Concrete test report" },
  { value: "form_16",       label: "Form 16 — Engineering sign-off" },
  { value: "other",         label: "Other" },
] as const;

interface HopPack {
  pack_id: string;
  pack_number: string;
  title: string | null;
  status: string;
}

interface HopAssetRow {
  asset_id:        string;
  pack_id:         string;
  asset_type:      "photo" | "document";
  storage_path:    string;
  file_name:       string | null;
  file_size_bytes: number | null;
  mime_type:       string | null;
  category:        { code: string; display_name: string } | null;
}

interface ProjectDocRow {
  id:              string;
  file_name:       string;
  file_size_bytes: number | null;
  mime_type:       string | null;
  doc_type:        string | null;
  uploaded_at:     string;
}

interface ContactRow {
  contact_id: string;
  full_name:  string;
  email:      string;
  role_title: string | null;
}

// -----------------------------------------------------------------------------

export default function NewHopSubmissionScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user          = useRequireAuth();
  const navigate      = useNavigate();

  if (!hasRole(user, "manager")) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-text-muted text-sm">Manager+ only.</div></Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [title, setTitle]           = useState("");
  const [notes, setNotes]           = useState("");
  // Photo selection comes from the basket (managed via the HOP gallery
  // toggles). On this screen the manager only changes bundle layout +
  // excludes per-submission temporarily. To remove from basket entirely,
  // the manager goes back to the gallery and unticks.
  const [excludedPhotos, setExcludedPhotos] = useState<Set<string>>(new Set());
  const [photoBundles,  setPhotoBundles]    = useState<Map<string, number>>(new Map());
  const [includedDocs,  setIncludedDocs]    = useState<Map<string, { kind: "hop" | "project"; type: string }>>(new Map());
  const [pickedRecipients, setPickedRecipients] = useState<Set<string>>(new Set());
  const [error,    setError]    = useState<string | null>(null);
  const [issuedUrl, setIssuedUrl] = useState<string | null>(null);
  const [issuedRecipients, setIssuedRecipients] = useState<{ email: string; sent: boolean; error: string | null }[]>([]);

  // T-160 — read the basket for this project (the draft submission).
  const { draftSubmissionId, basketAssetIds, basketSize, isLoading: basketLoading } = useAcceptanceBasket(projectId);

  // Photo preview lightbox — click "Eye" on a row to load + show.
  const [previewUrl,   setPreviewUrl]   = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);

  // Constants for the auto-distribute pass — defined here, used after
  // photos is declared further down. The actual autoDistribute callback
  // is defined later (after photos exists) to avoid temporal-dead-zone
  // errors on initial render.
  const TARGET_BUNDLE_MB    = 49;   // 1MB headroom under the 50MB ceiling
  const TARGET_BUNDLE_BYTES = TARGET_BUNDLE_MB * 1024 * 1024;
  const DEFAULT_PHOTO_BYTES = 2.5 * 1024 * 1024;

  async function openPreview(storagePath: string, label: string) {
    setPreviewLabel(label);
    setPreviewUrl("");          // sentinel — opens modal in loading state
    setPreviewLoading(true);
    const url = await getAssetSignedUrl(storagePath, 600);
    setPreviewUrl(url);
    setPreviewLoading(false);
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------
  const { data: project } = useQuery({
    queryKey: ["new_hop_sub_project", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, client_id,
          client:client_id(legal_name, trading_name)
        `)
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Load packs (used for "From HOP packs" doc picker; pack picker UI removed)
  const { data: packs = [] } = useQuery({
    queryKey: ["new_hop_sub_packs", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select("pack_id, pack_number, title, status")
        .eq("project_id", projectId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as HopPack[];
    },
  });
  const allPackIds = useMemo(() => packs.map(p => p.pack_id), [packs]);

  // Photos: load only the basket (asset_ids ticked across packs).
  const basketAssetIdList = useMemo(() => Array.from(basketAssetIds), [basketAssetIds]);
  const { data: assets = [] } = useQuery({
    queryKey: ["new_hop_sub_basket_assets", basketAssetIdList],
    enabled: basketAssetIdList.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_assets")
        .select(`
          asset_id, pack_id, asset_type, storage_path, file_name,
          file_size_bytes, mime_type,
          category:category_id(code, display_name)
        `)
        .in("asset_id", basketAssetIdList)
        .order("captured_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as HopAssetRow[];
    },
  });

  // Documents from HOP packs: pull doc-type assets across ALL of the
  // project's packs (not just the basket-photo packs). Manager picks which
  // to include, just like before.
  const { data: hopPackDocs = [] } = useQuery({
    queryKey: ["new_hop_sub_pack_docs", allPackIds],
    enabled: allPackIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_assets")
        .select(`
          asset_id, pack_id, asset_type, storage_path, file_name,
          file_size_bytes, mime_type,
          category:category_id(code, display_name)
        `)
        .in("pack_id", allPackIds)
        .eq("asset_type", "document")
        .order("captured_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as HopAssetRow[];
    },
  });

  const { data: projectDocs = [] } = useQuery({
    queryKey: ["new_hop_sub_project_docs", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bid_documents")
        .select("id, file_name, file_size_bytes, mime_type, doc_type, uploaded_at")
        .eq("project_id", projectId!)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProjectDocRow[];
    },
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ["new_hop_sub_contacts", project?.client_id],
    enabled: !!project?.client_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_contacts")
        .select("contact_id, full_name, email, role_title")
        .eq("client_id", project!.client_id!)
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as ContactRow[];
    },
  });

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const photos = useMemo(
    () => assets.filter(a => a.asset_type === "photo"),
    [assets],
  );
  const hopDocs = hopPackDocs;

  const packsByCat = useMemo(() => {
    // Group photos by pack -> category for cleaner display
    const out: Record<string, Record<string, HopAssetRow[]>> = {};
    for (const ph of photos) {
      const pid = ph.pack_id;
      const cat = ph.category?.display_name ?? "Uncategorised";
      out[pid] ??= {};
      out[pid][cat] ??= [];
      out[pid][cat].push(ph);
    }
    return out;
  }, [photos]);

  const packLookup = useMemo(() => {
    const m = new Map<string, HopPack>();
    for (const p of packs) m.set(p.pack_id, p);
    return m;
  }, [packs]);

  const includedPhotos = photos.filter(p => !excludedPhotos.has(p.asset_id));
  const bundleSizes    = useMemo(() => {
    const out: Record<number, { count: number; bytes: number }> = {};
    for (const p of includedPhotos) {
      const b = photoBundles.get(p.asset_id) ?? 1;
      out[b] ??= { count: 0, bytes: 0 };
      out[b].count += 1;
      out[b].bytes += p.file_size_bytes ?? 0;
    }
    return out;
  }, [includedPhotos, photoBundles]);

  // Auto-distribute included photos into 45MB bundles (5MB headroom under
  // the 50MB hard ceiling). Sequential packing by capture order. Manager
  // can re-run any time after deselecting noise, or override per-row.
  // Defined here (not earlier) because `photos` must exist first.
  const autoDistribute = useCallback(() => {
    let bundle = 1;
    let bytes  = 0;
    const next = new Map<string, number>();
    for (const p of photos) {
      if (excludedPhotos.has(p.asset_id)) continue;
      const size = p.file_size_bytes ?? DEFAULT_PHOTO_BYTES;
      if (bytes + size > TARGET_BUNDLE_BYTES && bytes > 0) {
        bundle += 1;
        bytes   = 0;
      }
      next.set(p.asset_id, bundle);
      bytes += size;
    }
    setPhotoBundles(next);
  }, [photos, excludedPhotos]);

  // First time photos arrive, auto-distribute. Don't override manager's
  // manual edits if they've already started tweaking.
  useEffect(() => {
    if (photos.length > 0 && photoBundles.size === 0) {
      autoDistribute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const submit = useMutation({
    mutationFn: async () => {
      if (!project) throw new Error("Project not loaded.");
      if (!draftSubmissionId) throw new Error("No basket — go pick photos from the HOP packs first.");
      if (includedPhotos.length === 0 && includedDocs.size === 0) throw new Error("Empty submission — pick at least one photo or document.");
      if (pickedRecipients.size === 0) throw new Error("Pick at least one recipient.");

      const submissionId = draftSubmissionId;

      // 1. Update the draft with title + notes (no need to create — basket
      //    already created the submission when first photo was added).
      const { error: updateErr } = await supabase
        .from("hop_submissions")
        .update({
          title:  title.trim() || null,
          notes:  notes.trim() || null,
        })
        .eq("submission_id", submissionId);
      if (updateErr) throw updateErr;

      // 2. Derive packs from the basket photos and link them.
      const packsInBasket = new Set(includedPhotos.map(p => p.pack_id));
      // Wipe existing pack links + recipients + docs so re-submits are idempotent
      await supabase.from("hop_submission_packs").delete().eq("submission_id", submissionId);
      await supabase.from("hop_submission_documents").delete().eq("submission_id", submissionId);
      await supabase.from("hop_submission_recipients").delete().eq("submission_id", submissionId);
      if (packsInBasket.size > 0) {
        const { error } = await supabase.from("hop_submission_packs").insert(
          Array.from(packsInBasket).map(pack_id => ({ submission_id: submissionId, pack_id })),
        );
        if (error) throw error;
      }

      // 3. Photos — re-set bundles + sequences. Wipe and re-insert with the
      //    final bundle layout (basket only had defaults).
      await supabase.from("hop_submission_photos").delete().eq("submission_id", submissionId);

      // Photos with bundle layout — derive sequence per bundle for renames
      // Group included photos by bundle, then by pack/category, then number
      // them sequentially within each (bundle, category) pair.
      const photoRowsByBundle = new Map<number, HopAssetRow[]>();
      for (const p of includedPhotos) {
        const b = photoBundles.get(p.asset_id) ?? 1;
        if (!photoRowsByBundle.has(b)) photoRowsByBundle.set(b, []);
        photoRowsByBundle.get(b)!.push(p);
      }
      const photoInsertRows: any[] = [];
      for (const [bundle, rows] of photoRowsByBundle) {
        // Stable sort: first by category, then by file name / capture order
        rows.sort((a, b) =>
          (a.category?.code ?? "").localeCompare(b.category?.code ?? "")
          || a.asset_id.localeCompare(b.asset_id),
        );
        let seq = 1;
        for (const r of rows) {
          photoInsertRows.push({
            submission_id:      submissionId,
            asset_id:           r.asset_id,
            bundle_number:      bundle,
            sequence_in_bundle: seq++,
          });
        }
      }
      if (photoInsertRows.length > 0) {
        const { error } = await supabase.from("hop_submission_photos").insert(photoInsertRows);
        if (error) throw error;
      }

      // 4. Docs
      const docInsertRows: any[] = [];
      let docOrder = 0;
      for (const [id, meta] of includedDocs) {
        docInsertRows.push({
          submission_id: submissionId,
          hop_asset_id:  meta.kind === "hop"     ? id : null,
          project_doc_id: meta.kind === "project" ? id : null,
          doc_type:      meta.type,
          display_order: docOrder++,
        });
      }
      if (docInsertRows.length > 0) {
        const { error } = await supabase.from("hop_submission_documents").insert(docInsertRows);
        if (error) throw error;
      }

      // 5. Recipients
      if (pickedRecipients.size > 0) {
        const { error } = await supabase.from("hop_submission_recipients").insert(
          Array.from(pickedRecipients).map(contact_id => ({
            submission_id: submissionId,
            contact_id,
          })),
        );
        if (error) throw error;
      }

      // 6. Issue via worker → mint magic token + flip to sent
      const issueRes = await apiFetch(`/hop/submissions/${submissionId}/issue`, {
        method: "POST",
        body:   JSON.stringify({ expiry_days: 7 }),
      });
      if (!issueRes.ok) {
        const detail = await issueRes.text().catch(() => "");
        throw new Error(`Issue failed: ${detail}`);
      }
      const issued = await issueRes.json() as {
        token: string;
        accept_url: string;
        emails?: Array<{ contact_id: string; ok: boolean; messageId?: string; error?: string }>;
      };

      // Build recipient emails for the success modal + email-send status
      const emails = contacts
        .filter(c => pickedRecipients.has(c.contact_id))
        .map(c => {
          const sendResult = issued.emails?.find(e => e.contact_id === c.contact_id);
          return {
            email:   c.email,
            sent:    !!sendResult?.ok,
            error:   sendResult?.error ?? null,
          };
        });
      return { url: issued.accept_url, emails };
    },
    onSuccess: (out) => {
      setIssuedUrl(out.url);
      setIssuedRecipients(out.emails);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                : (err as { message?: string })?.message ?? "Submit failed";
      setError(msg);
    },
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!project) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Spinner/></div>;
  }

  if (issuedUrl) {
    return (
      <IssuedSuccessPanel
        url={issuedUrl}
        emails={issuedRecipients}
        onDone={() => navigate(`/projects/${projectId}`)}
      />
    );
  }

  const totalPhotosIncluded = includedPhotos.length;
  const totalDocsIncluded   = includedDocs.size;
  const totalDataMb         = Object.values(bundleSizes).reduce((acc, b) => acc + b.bytes, 0) / (1024 * 1024);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-16">
      <Link
        to={`/projects/${projectId}`}
        className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2"
      >
        <ArrowLeft size={14}/> Back to project
      </Link>

      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">New HOP submission</h1>
        <div className="text-text-muted text-[13px] mt-1">
          Build a client-ready acceptance pack from {project.project_code}{" "}
          — {project.project_name}.
          Pick HOP packs → review photos → group bundles → pick docs → pick recipients → send.
        </div>
      </div>

      {/* Title */}
      <Card className="mb-4">
        <div className="p-4 grid gap-3">
          <Field label="Submission title (optional)" hint="Defaults to the auto-generated number if blank.">
            <Input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Andergrove HOP — Stage 1"
            />
          </Field>
          <Field label="Notes for the client (optional)">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
              placeholder="Any context worth including in the acceptance pack…"
            />
          </Field>
        </div>
      </Card>

      {/* Basket summary — replaces the old pack picker. Shows what was
          ticked across HOP categories. Empty state directs the manager
          back to pick photos. */}
      <Card className="mb-4">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
              Acceptance pack basket
            </div>
            <div className="text-[11px] text-text-muted">
              {basketSize} photo{basketSize === 1 ? "" : "s"}
              {basketSize > 0 && ` · across ${new Set(photos.map(p => p.pack_id)).size} pack(s)`}
            </div>
          </div>
          {basketSize === 0 && !basketLoading ? (
            <div className="py-6">
              <EmptyState
                title="Basket is empty"
                description="Browse the project's HOP packs and tap the basket icon on each photo you want to include in this submission. Then come back here to bundle and dispatch."
              />
              <div className="mt-3 flex justify-center">
                <Link to={`/projects/${projectId}/hop`}>
                  <Button variant="primary">Open HOP packs</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-[12px] text-text-muted">
              {Array.from(new Set(photos.map(p => p.pack_id))).map(pid => {
                const pack = packs.find(p => p.pack_id === pid);
                if (!pack) return null;
                const count = photos.filter(p => p.pack_id === pid).length;
                return (
                  <div key={pid} className="flex items-center gap-2 py-0.5">
                    <span className="font-mono text-[11px] text-text-muted">{pack.pack_number}</span>
                    <span>{pack.title ?? "—"}</span>
                    <span className="text-text-dim">·</span>
                    <span>{count} photo{count === 1 ? "" : "s"}</span>
                  </div>
                );
              })}
              <div className="mt-2 text-[11px] text-text-dim italic">
                Tip: keep adding from the HOP gallery — open another pack and the basket grows.
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Photo review + bundle assignment */}
      {basketSize > 0 && (
        <Card className="mb-4">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
                Photos — review &amp; group
              </div>
              <div className="text-[11px] text-text-muted">
                {totalPhotosIncluded} of {photos.length} included · {totalDataMb.toFixed(0)} MB
              </div>
            </div>

            {photos.length === 0 ? (
              <div className="text-[13px] text-text-muted">No photos in selected packs yet.</div>
            ) : (
              <>
                {/* Bundle size summary + auto-distribute action */}
                <div className="mb-3 flex flex-wrap gap-2 items-center">
                  {Object.entries(bundleSizes).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([b, info]) => {
                    const overLimit = info.bytes > 50 * 1024 * 1024;
                    return (
                      <span
                        key={b}
                        className={`text-[11px] font-mono px-2 py-0.5 rounded border ${
                          overLimit
                            ? "border-warn/40 bg-warn/8 text-warn"
                            : "border-line text-text-muted"
                        }`}
                        title={overLimit ? "Bundle exceeds 50MB — may want to split" : ""}
                      >
                        Bundle {b}: {info.count} · {(info.bytes / 1024 / 1024).toFixed(0)}MB
                        {overLimit && " ⚠"}
                      </span>
                    );
                  })}
                  <button
                    type="button"
                    onClick={autoDistribute}
                    className="ml-auto text-[11px] px-2 py-0.5 rounded border border-accent/40 text-accent hover:bg-accent/10"
                    title="Re-pack included photos into ~45MB bundles in capture order"
                  >
                    Auto-pack into 45MB bundles
                  </button>
                </div>

                {/* Photos grouped by pack/category */}
                {Object.entries(packsByCat).map(([packId, byCat]) => {
                  const pack = packLookup.get(packId);
                  return (
                    <div key={packId} className="mb-4">
                      <div className="text-[11px] font-medium text-text mb-2">
                        {pack?.pack_number} — {pack?.title ?? "—"}
                      </div>
                      {Object.entries(byCat).map(([cat, rows]) => (
                        <div key={cat} className="mb-3 ml-2">
                          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">
                            {cat} ({rows.length})
                          </div>
                          <div className="space-y-1">
                            {rows.map(r => {
                              const included = !excludedPhotos.has(r.asset_id);
                              const bundle   = photoBundles.get(r.asset_id) ?? 1;
                              const fileLabel = r.file_name ?? r.storage_path.split("/").pop() ?? "(no name)";
                              return (
                                <div
                                  key={r.asset_id}
                                  className={`flex items-center gap-2 px-2 py-1 rounded text-[12px] ${included ? "" : "opacity-40"}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={included}
                                    onChange={e => {
                                      const next = new Set(excludedPhotos);
                                      e.target.checked ? next.delete(r.asset_id) : next.add(r.asset_id);
                                      setExcludedPhotos(next);
                                    }}
                                    className="accent-accent"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => openPreview(r.storage_path, fileLabel)}
                                    className="text-text-muted hover:text-accent p-0.5 rounded inline-flex items-center"
                                    title="Preview photo"
                                  >
                                    <Eye size={14}/>
                                  </button>
                                  <span className="flex-1 truncate font-mono text-[11px]">
                                    {fileLabel}
                                  </span>
                                  <span className="text-[10px] text-text-dim">
                                    {r.file_size_bytes ? `${(r.file_size_bytes / 1024 / 1024).toFixed(1)}MB` : ""}
                                  </span>
                                  <select
                                    value={bundle}
                                    onChange={e => {
                                      const next = new Map(photoBundles);
                                      next.set(r.asset_id, parseInt(e.target.value, 10));
                                      setPhotoBundles(next);
                                    }}
                                    disabled={!included}
                                    className="bg-ink border border-line rounded px-1.5 py-0.5 text-[10px] text-text"
                                  >
                                    {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                                      <option key={n} value={n}>Bundle {n}</option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </Card>
      )}

      {/* Documents */}
      {basketSize > 0 && (
        <Card className="mb-4">
          <div className="p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-2">
              Documents ({totalDocsIncluded} included)
            </div>

            {hopDocs.length === 0 && projectDocs.length === 0 ? (
              <div className="text-[13px] text-text-muted">No documents on file yet.</div>
            ) : (
              <div className="space-y-3">
                {hopDocs.length > 0 && (
                  <div>
                    <div className="text-[10px] text-text-muted mb-1">From HOP packs</div>
                    {hopDocs.map(d => (
                      <DocPickerRow
                        key={d.asset_id}
                        id={d.asset_id}
                        kind="hop"
                        name={d.file_name ?? d.storage_path.split("/").pop() ?? "—"}
                        size={d.file_size_bytes}
                        included={includedDocs}
                        setIncluded={setIncludedDocs}
                      />
                    ))}
                  </div>
                )}
                {projectDocs.length > 0 && (
                  <div>
                    <div className="text-[10px] text-text-muted mb-1">From project documents</div>
                    {projectDocs.map(d => (
                      <DocPickerRow
                        key={d.id}
                        id={d.id}
                        kind="project"
                        name={d.file_name}
                        size={d.file_size_bytes}
                        included={includedDocs}
                        setIncluded={setIncludedDocs}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Recipients */}
      <Card className="mb-4">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
              Recipients ({pickedRecipients.size} selected)
            </div>
            <Link to={`/clients/${project.client_id}`} className="text-[11px] text-accent hover:underline">
              Manage contacts
            </Link>
          </div>

          {contacts.length === 0 ? (
            <div className="text-[13px] text-text-muted">
              No contacts on file for this client. Add one from the client detail page first.
            </div>
          ) : (
            <div className="space-y-1.5">
              {contacts.map(c => {
                const checked = pickedRecipients.has(c.contact_id);
                return (
                  <label key={c.contact_id} className="flex items-center gap-2 cursor-pointer text-[13px]">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={e => {
                        const next = new Set(pickedRecipients);
                        e.target.checked ? next.add(c.contact_id) : next.delete(c.contact_id);
                        setPickedRecipients(next);
                      }}
                      className="accent-accent"
                    />
                    <span className="font-medium">{c.full_name}</span>
                    <span className="text-text-muted text-[11px]">{c.email}</span>
                    {c.role_title && <span className="text-text-dim text-[11px]">· {c.role_title}</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {error && (
        <Card className="mb-4 border-danger/30">
          <div className="p-4 flex items-start gap-3 text-sm text-danger">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
            <span>{error}</span>
          </div>
        </Card>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <Link to={`/projects/${projectId}`}>
          <Button variant="ghost" disabled={submit.isPending}>Cancel</Button>
        </Link>
        <Button
          variant="primary"
          icon={Send}
          onClick={() => { setError(null); submit.mutate(); }}
          disabled={submit.isPending}
        >
          {submit.isPending ? "Submitting…" : "Issue submission"}
        </Button>
      </div>

      {/* Photo preview lightbox — clicking the eye icon on a photo row
          loads a signed URL and renders the image in a fullscreen modal.
          Tap outside or press X to close. */}
      {previewUrl !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex flex-col"
          onClick={() => setPreviewUrl(null)}
          role="dialog"
          aria-label={previewLabel}
        >
          <div className="flex items-center justify-between p-4 text-white">
            <div className="text-sm font-mono truncate">{previewLabel}</div>
            <button
              onClick={() => setPreviewUrl(null)}
              className="p-2 rounded hover:bg-white/10"
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
          </div>
          <div
            className="flex-1 flex items-center justify-center p-4 min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            {previewLoading || !previewUrl ? (
              <div className="text-white/60 text-sm">Loading…</div>
            ) : (
              <img
                src={previewUrl}
                alt={previewLabel}
                className="max-w-full max-h-full object-contain select-none"
              />
            )}
          </div>
          <div className="text-center text-white/60 text-xs pb-4">
            Tap outside or press the X to close
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function DocPickerRow({
  id, kind, name, size, included, setIncluded,
}: {
  id:           string;
  kind:         "hop" | "project";
  name:         string;
  size:         number | null;
  included:     Map<string, { kind: "hop" | "project"; type: string }>;
  setIncluded:  (m: Map<string, { kind: "hop" | "project"; type: string }>) => void;
}) {
  const inc = included.get(id);
  const isOn = !!inc;
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded text-[12px] hover:bg-ink-raised/30">
      <input
        type="checkbox"
        checked={isOn}
        onChange={e => {
          const next = new Map(included);
          if (e.target.checked) next.set(id, { kind, type: inc?.type ?? "rlm_pdf" });
          else next.delete(id);
          setIncluded(next);
        }}
        className="accent-accent"
      />
      <span className="flex-1 truncate">{name}</span>
      {size != null && <span className="text-[10px] text-text-dim">{(size / 1024 / 1024).toFixed(1)}MB</span>}
      {isOn && (
        <select
          value={inc!.type}
          onChange={e => {
            const next = new Map(included);
            next.set(id, { kind, type: e.target.value });
            setIncluded(next);
          }}
          className="bg-ink border border-line rounded px-1.5 py-0.5 text-[11px] text-text"
        >
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function IssuedSuccessPanel({
  url, emails, onDone,
}: {
  url:    string;
  emails: { email: string; sent: boolean; error: string | null }[];
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto pb-16">
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-ok/15 text-ok flex items-center justify-center">
              <Check size={18}/>
            </div>
            <div>
              <div className="text-[16px] font-semibold">Submission issued</div>
              <div className="text-[12px] text-text-muted">Magic link is valid for 7 days.</div>
            </div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Acceptance link</div>
          <div className="flex items-center gap-2 mb-4">
            <input
              readOnly
              value={url}
              className="flex-1 bg-ink border border-line rounded px-2.5 py-1.5 text-[12px] font-mono text-text"
              onFocus={e => e.target.select()}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={Copy}
              onClick={async () => {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" icon={ExternalLink}>Open</Button>
            </a>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Recipients</div>
          <div className="text-[13px] text-text">
            {emails.length === 0 ? <span className="text-text-muted">—</span> : emails.map(e => (
              <div key={e.email} className="flex items-center gap-2">
                <Mail size={12} className={e.sent ? "text-ok" : e.error ? "text-warn" : "text-text-muted"}/>
                <span>{e.email}</span>
                {e.sent && <span className="text-[10px] text-ok">· sent</span>}
                {e.error && (
                  <span className="text-[10px] text-warn truncate" title={e.error}>· not sent ({e.error.slice(0, 60)})</span>
                )}
              </div>
            ))}
          </div>
          {emails.some(e => !e.sent) && (
            <div className="text-[11px] text-text-muted mt-2">
              {emails.every(e => !e.sent && !e.error)
                ? "Resend not configured yet — copy the link above and email it manually."
                : "Some emails couldn't be sent. Copy the link and forward to those recipients manually."}
            </div>
          )}
          {emails.length > 0 && emails.every(e => e.sent) && (
            <div className="text-[11px] text-ok mt-2">
              All emails delivered to Resend. Recipients should see them in their inbox shortly.
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <Button variant="primary" onClick={onDone}>Done</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
